import React, { useState } from 'react'
import {
  Select,
  SelectValue,
  Button,
  Popover,
  ListBox,
  ListBoxItem,
} from 'react-aria-components'
import { DesignSystem } from '../types'

interface HeaderProps {
  systems: DesignSystem[]
  activeSystemId: string | null
  onSystemSelect: (id: string) => void
  onCreateNew: () => void
  onExport: () => void
  onDelete: (id: string) => void
  onDuplicate: (id: string) => void
}

export function Header({
  systems,
  activeSystemId,
  onSystemSelect,
  onCreateNew,
  onExport,
  onDelete,
  onDuplicate,
}: HeaderProps) {
  const [showMenu, setShowMenu] = useState(false)
  const activeSystem = systems.find((s) => s.id === activeSystemId)

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Logo + Title */}
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2 text-gray-900 hover:text-gray-700 transition-colors cursor-pointer">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="font-semibold hidden sm:inline">Design System</span>
            </a>
          </div>

          {/* Center: System Selector */}
          <div className="flex-1 max-w-xs">
            {systems.length > 0 ? (
              <Select
                selectedKey={activeSystemId || ''}
                onSelectionChange={(key) => {
                  if (key === '__new__') {
                    onCreateNew()
                  } else {
                    onSystemSelect(key as string)
                  }
                }}
              >
                <Button className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent cursor-pointer">
                  <SelectValue className="truncate font-medium">
                    {() => activeSystem?.name || 'Select a system'}
                  </SelectValue>
                  <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
                <Popover className="w-[--trigger-width] bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  <ListBox className="max-h-60 overflow-auto outline-none p-1">
                    {systems.map((system) => (
                      <ListBoxItem
                        key={system.id}
                        id={system.id}
                        className="px-3 py-2 text-sm cursor-pointer rounded-md outline-none data-[focused]:bg-gray-100 data-[selected]:bg-gray-900 data-[selected]:text-white"
                      >
                        {system.name}
                      </ListBoxItem>
                    ))}
                    <ListBoxItem
                      id="__new__"
                      className="px-3 py-2 text-sm cursor-pointer rounded-md outline-none text-blue-600 data-[focused]:bg-blue-50 border-t border-gray-100 mt-1"
                    >
                      + Create New System
                    </ListBoxItem>
                  </ListBox>
                </Popover>
              </Select>
            ) : (
              <button
                onClick={onCreateNew}
                className="w-full px-3 py-2 text-sm font-medium text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50 transition-colors cursor-pointer"
              >
                + Create Your First System
              </button>
            )}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {activeSystem && (
              <>
                {/* More Actions Menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowMenu(!showMenu)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                    title="More actions"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                    </svg>
                  </button>
                  {showMenu && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowMenu(false)}
                      />
                      <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                        <button
                          onClick={() => {
                            onDuplicate(activeSystem.id)
                            setShowMenu(false)
                          }}
                          className="w-full px-4 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2 cursor-pointer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Duplicate
                        </button>
                        <button
                          onClick={() => {
                            if (confirm(`Delete "${activeSystem.name}"?`)) {
                              onDelete(activeSystem.id)
                            }
                            setShowMenu(false)
                          }}
                          className="w-full px-4 py-2 text-sm text-left text-red-600 hover:bg-red-50 flex items-center gap-2 cursor-pointer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Export Button */}
                <button
                  onClick={onExport}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <span className="hidden sm:inline">Export</span>
                </button>
              </>
            )}

            {/* Back Link */}
            <a
              href="/"
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              title="Back to Nimble Labs"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </header>
  )
}
